import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { defaultLocale, supportedLocales } from '@shared/locales';

/**
 * UI-chrome translations only (nav, footer, forms, page furniture). Page
 * *content* (headlines, page copy, FAQ…) lives in content/ and is looked up
 * separately by src/web/lib/content.ts, keyed off the URL's locale.
 *
 * Locale JSON files are loaded by glob so adding a language is adding
 * `locales/{code}.json` plus an entry in `supportedLocales` — no import list
 * to keep in step.
 *
 * Initialized with the default locale and no LanguageDetector plugin. The
 * actual language of a prerendered page comes from its URL: `render()` in
 * entry-server.tsx (and main.tsx on the client) call `changeLanguage` from
 * the path *before* the first paint, so the HTML and the hydrate match.
 * A browser that prefers another language is sent to that language's URL
 * (the Worker, from Accept-Language, and I18nBootstrap, from
 * navigator.languages) rather than having this instance switch under a
 * hydrated English tree. See src/web/i18n/I18nBootstrap.tsx.
 */
const localeModules = import.meta.glob('./locales/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, Record<string, unknown>>;

const resources = Object.fromEntries(
  supportedLocales.map((locale) => {
    const messages = localeModules[`./locales/${locale}.json`];
    if (!messages) {
      throw new Error(`Missing i18n resource src/web/i18n/locales/${locale}.json`);
    }
    return [locale, { translation: messages }];
  }),
);

void i18n.use(initReactI18next).init({
  resources,
  lng: defaultLocale,
  fallbackLng: defaultLocale,
  supportedLngs: [...supportedLocales],
  interpolation: { escapeValue: false },
});

export default i18n;
